import { Ref } from "vue";
import { CrowdHandlerWRState } from '../types/crowdhandler';

let nextPollInterval: ReturnType<typeof setInterval> | null = null;

// Sets `ch.shuffling` to true while the current time is within 1 minute of the
// room's `onsale`. Templates use the flag to swap copy:
// `ch.shuffling ? 'Shuffling positions' : 'Checking in'`.
//
// Rule (from the user's spec):
//   - "Shuffling positions" while now is between onsale and onsale + 60s.
//   - "Checking in" otherwise.
//   e.g. onsale 13:00:00, now 13:00:45 → shuffling=true (45s in window).
//
// Uses the live clock (`Date.now()`) rather than the poll's `requested`
// timestamp so the flag is correct the instant the countdown flips (when no
// fresh poll has happened yet) — at completion now ≈ onsale, so shuffling=true.
//
// Always false when not in the checking-in state (no `isActive`, or a position
// is set), or when onsale is missing/unparseable.
const updateShuffling = (ch: Ref<CrowdHandlerWRState>) => {
    if (!ch.value.isActive || ch.value.position) {
        ch.value.shuffling = false;
        return;
    }
    if (!ch.value.onsale) {
        ch.value.shuffling = false;
        return;
    }
    const onsaleMs = Date.parse(ch.value.onsale);
    if (Number.isNaN(onsaleMs)) {
        ch.value.shuffling = false;
        return;
    }
    const sinceOnsaleMs = Date.now() - onsaleMs;
    ch.value.shuffling = sinceOnsaleMs >= 0 && sinceOnsaleMs <= 60000;
};

const updateNextPollAnimation = (ch: Ref<CrowdHandlerWRState>, ttl: number = 60) => {
    if (nextPollInterval !== null) {
        clearInterval(nextPollInterval);
        nextPollInterval = null;
    }

    // Start the bar at "time elapsed since the server cached this response"
    // so that browsers sharing a cached response see different starting
    // positions, and a refreshed browser doesn't pay a full ttl again.
    //
    // Stays at 0 when elapsed is negative (clock skew) or beyond ttl (server
    // is replaying a stale cached response). Without the upper guard, a
    // stale cache would pin the bar at 100% from the start of every cycle.
    let startVal = 0;
    if (ch.value.requested) {
        const requestedMs = Date.parse(ch.value.requested);
        if (!Number.isNaN(requestedMs)) {
            const elapsed = Math.floor((Date.now() - requestedMs) / 1000);
            if (elapsed >= 0 && elapsed <= ttl) {
                startVal = elapsed;
            }
        }
    }

    ch.value.nextPoll = startVal;
    ch.value.checking = Math.round((startVal / ttl) * 100);

    // Evaluate the shuffling flag once, at poll arrival, so the bar and
    // label change together. Stays constant for the bar cycle — the next
    // poll's call re-evaluates from the fresh `requested` timestamp.
    updateShuffling(ch);

    nextPollInterval = setInterval(() => {
        ch.value.nextPoll++;
        ch.value.checking = Math.round((ch.value.nextPoll / ttl) * 100);
        if (ch.value.nextPoll >= ttl) {
            if (nextPollInterval !== null) {
                clearInterval(nextPollInterval);
                nextPollInterval = null;
            }
        }
    }, 1000);
};

const isNextPollAnimating = (): boolean => nextPollInterval !== null;

export { updateNextPollAnimation, isNextPollAnimating, updateShuffling };
